import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Accordion, Modal, ModalHeader, ModalContent, Pill, ProgressBar } from '@sainsburys-tech/bolt'
import { ChevronIcon } from '@sainsburys-tech/bolt-icons'
import { useVocab } from '@sainsburys-tech/bolt-vocab-provider'
import ReviewsSummaryBox from '../ReviewsSummaryBox/ReviewsSummaryBox'

import * as Styled from './ReviewsSummary.styles'

const BRAND = process.env.BRAND || 'ARGOS'

const roundDP = num => Math.round(num * 10) / 10

const BuildSubattributes = ({ secondaryRatings, dataTest }) => {
  const subattributesItem = (title, value) => {
    if (title === 'Fit') return null
    return (
      <Styled.BarContainer key={title} data-test={`${dataTest}-subattributes`}>
        <div data-test={`${dataTest}-subattributes-heading`}>
          <span>{title}</span>
          <Styled.Subheading>{roundDP(value)}/5</Styled.Subheading>
        </div>
        <ProgressBar
          value={value}
          scale={5}
          numberOfSegments={5}
          showCount={false}
          data-test={`${dataTest}-subattributes-progress-bar`}
        />
      </Styled.BarContainer>
    )
  }
  const attributeBars = []
  Object.keys(secondaryRatings).forEach(item => {
    if (item !== 'Fit') attributeBars.push(subattributesItem(item, secondaryRatings[item]))
  })

  if (attributeBars.length > 1) {
    attributeBars.sort((itemA, itemB) => {
      return itemA.key < itemB.key ? -1 : itemA.key > itemB.key // A < B return -1 | A > B return 1 | return 0
    })
  }

  return attributeBars
}
// eslint-disable-next-line
const RatingBreakdown = ({ sortedRatings, reviewCount, pillState, pillSetState }) => {
  const ratingBars = []
  const max = Math.max(...sortedRatings).toString().length

  for (let index = 0; index < 5; index += 1) {
    ratingBars.push(
      <Styled.RatingBreakdown key={index}>
        <Pill
          as={Styled.Pill}
          label={`${5 - index} Star`}
          disabled={!sortedRatings[index]}
          onClick={() => (sortedRatings[index] ? pillSetState(index) : null)}
          active={pillState[index]}
          data-test={`${5 - index}-star-pill`}
        />

        <ProgressBar
          value={sortedRatings[index]}
          maxCountOffset={max + 1}
          scale={reviewCount}
          data-test={`${5 - index}-star-progress-bar`}
        />
      </Styled.RatingBreakdown>
    )
  }
  return ratingBars
}

const FitRatingBreakdown = ({ fitDistribution, fitCount }) => {
  const ratingBars = []
  const labels = ['Too big', 'A little big', 'True to size', 'A little small', 'Too small']
  const max = Math.max(...fitDistribution).toString().length

  for (let index = 0; index < 5; index += 1) {
    ratingBars.push(
      <Styled.RatingBreakdown key={index}>
        <Styled.FitLabels>{labels[index]}</Styled.FitLabels>
        <ProgressBar
          value={fitDistribution.find(item => item.RatingValue === 5 - index).Count}
          maxCountOffset={max + 1}
          scale={fitCount}
          data-test={`${5 - index}-star-fit-progress-bar`}
        />
      </Styled.RatingBreakdown>
    )
  }
  return ratingBars
}

const FitToText = fit => {
  switch (fit) {
    case 'big':
      return "'bigger than expected'"
    case 'expected':
      return "'as expected'"
    case 'small':
      return "'smaller than expected'"
    default:
      return ''
  }
}

const FitAsExpected = ({ onClickFn, fitPercentage, fitName, dataTest }) => {
  const fitText = FitToText(fitName)
  return (
    <Styled.FitContainer data-test={`${dataTest}-fit-as-expected`}>
      <Styled.FitAsExpected onClick={() => onClickFn()}>
        Fitted {fitPercentage}% of customers {fitText}
        <ChevronIcon dir='right' height={24} width={24} fill='#0c509f' />
      </Styled.FitAsExpected>
    </Styled.FitContainer>
  )
}

const FitModal = ({ show, setShow, fitCount, fitDistribution, fitName, fitPercentage, dataTest }) => {
  return (
    <Modal show={show} height='auto' onClose={() => setShow(!show)} data-test={`${dataTest}-fit-modal`}>
      <ModalHeader>How it fits</ModalHeader>
      <ModalContent>
        <p>
          This item fitted {fitPercentage}% of customers {FitToText(fitName)}
        </p>
        <Styled.ModalBreakdown>
          <FitRatingBreakdown {...{ fitDistribution, fitCount }} />
        </Styled.ModalBreakdown>
      </ModalContent>
    </Modal>
  )
}

const ReviewsSummary = ({ 'data-test': dataTest, stats, ratingSummary, pillState, pillSetState }) => {
  const [show, setShow] = useState(false)

  const { BRAND_NAME } = useVocab()
  const { attributes = {} } = stats
  const {
    overallRating,
    ratingDistribution: ratings,
    recommendationPercentage,
    secondaryRatings = {},
    reviewCount
  } = attributes

  const { attributes: fitAttributes = {} } = ratingSummary
  const { fitDistribution, overallFit } = fitAttributes
  const { name: fitName, totalNoOfFitRatings: fitCount, percentage: fitPercentage } = overallFit

  const sortedRatings = []
  for (let index = 5; index > 0; index -= 1) {
    const matchedRating = ratings.find(item => item.RatingValue === index) || { Count: 0 }
    sortedRatings[5 - index] = matchedRating.Count
  }

  const leftBoxProps = { title: roundDP(overallRating).toString(), overallRating, message: `${reviewCount} reviews` }
  const rightBoxProps = { title: `${recommendationPercentage.toString()}%`, message: 'Recommended this product' }

  const titleProp = (
    <>
      <span data-test={`${dataTest}-title-reviews`}>{`About ${BRAND_NAME} reviews`}</span>
      <Styled.Trustmark
        src='//display.ugc.bazaarvoice.com/common/images/trustmark_icons/trustmark_en.png'
        alt='BazaarVoice TrustMark'
        lazy
      />
    </>
  )

  let boxText = null
  if (BRAND === 'ARGOS') {
    boxText = (
      <>
        Each review is verified as authentic by our{' '}
        <a href='https://www.bazaarvoice.com/legal/authenticity-policy/' target='_blank' rel='noreferrer'>
          independent third party
        </a>{' '}
        {`and we only publish reviews from customers or ${BRAND_NAME} Testers who have purchased or used the item.
        Customers are incentivised to submit product reviews as part of a prize draw.`}
      </>
    )
  } else if (BRAND === 'HABITAT') {
    boxText = (
      <>
        Each review is verified as authentic by our{' '}
        <a href='https://www.bazaarvoice.com/legal/authenticity-policy/' target='_blank' rel='noreferrer'>
          independent third party.
        </a>{' '}
        We only publish reviews from customers who have purchased the item. Customers are incentivised to submit product
        reviews as part of a prize draw.
      </>
    )
  } else if (BRAND === 'TU') {
    boxText = (
      <>
        Each review is verified as authentic by our{' '}
        <a href='https://www.bazaarvoice.com/legal/authenticity-policy/' target='_blank' rel='noreferrer'>
          independent third party.
        </a>{' '}
        Submissions via our post-purchase review email are incentivised by way of a prize draw.
      </>
    )
  }
  const onClickFn = () => setShow(!show)

  return (
    <Styled.Container data-test={dataTest}>
      <Styled.BoxContainer>
        <ReviewsSummaryBox {...leftBoxProps} />
        {!!recommendationPercentage && <ReviewsSummaryBox {...rightBoxProps} />}
      </Styled.BoxContainer>
      <Styled.AboutContainer>
        <Accordion.Item
          title={titleProp}
          as={Styled.AboutTitle}
          wrapperPadding={9}
          data-test={`${dataTest}-about-reviews`}
          index={`${dataTest}-about-reviews`}
        >
          <Styled.Text>{boxText}</Styled.Text>
        </Accordion.Item>
      </Styled.AboutContainer>
      <Styled.Subattributes>
        <BuildSubattributes {...{ secondaryRatings, dataTest }} />
      </Styled.Subattributes>
      <RatingBreakdown {...{ sortedRatings, reviewCount, pillState, pillSetState }} />
      {fitDistribution && (
        <>
          <Styled.FittingTitle>Fitting</Styled.FittingTitle>
          <FitAsExpected {...{ onClickFn, fitPercentage, fitName, dataTest }} />
          <FitModal {...{ show, setShow, fitDistribution, fitName, fitCount, fitPercentage, dataTest }} />
        </>
      )}
    </Styled.Container>
  )
}

ReviewsSummary.propTypes = {
  /**
   * Specify an optional data-test attribute for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Specify a stats object for regular review information
   */
  stats: PropTypes.shape({
    attributes: PropTypes.shape({
      overallRating: PropTypes.number,
      ratingDistribution: PropTypes.arrayOf(
        PropTypes.shape({
          Count: PropTypes.number,
          RatingValue: PropTypes.number
        })
      ),
      recommendationPercentage: PropTypes.number,
      reviewCount: PropTypes.number,
      secondaryRatings: PropTypes.object
    })
  }),

  /**
   * Specify ratingSummary attribute for Tu pages
   */
  ratingSummary: PropTypes.shape({
    attributes: PropTypes.shape({
      fitDistribution: PropTypes.arrayOf(
        PropTypes.shape({
          Count: PropTypes.number,
          RatingValue: PropTypes.number
        })
      ),
      overallFit: PropTypes.shape({
        name: PropTypes.string,
        totalNoOfFitRatings: PropTypes.number,
        percentage: PropTypes.number
      })
    })
  })
}

ReviewsSummary.defaultProps = {
  'data-test': 'reviews-summary'
}

export default ReviewsSummary
export { ReviewsSummary, FitModal, FitToText }
